import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';

import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { CHAT_HEADER_ICON_BUTTON_SX } from '@/modules/common/Chat/components/chatHeaderStyles';
import { COLORS } from '@/styles/tokens/colors';
import { UnreachableError } from '@/utils/errors';

import { ChatSideContent } from './chatSideContent.types';
import { useChatSideContentContext } from './context/chatSideContent.context';
import {
  CitationDocumentSideContentBody,
  CitationDocumentSideContentScope,
  CitationDocumentSideContentTitle,
} from './renderers/CitationDocumentSideContentRenderer';
import {
  DocumentSummarySideContentBody,
  DocumentSummarySideContentTitle,
} from './renderers/DocumentSummarySideContentRenderer';

// The scope/title/body switches are the render side of the ChatSideContent
// union: adding a union member without a case here fails compilation via the
// UnreachableError default.

/**
 * Mounts content-type-scoped context around the whole pane chrome (header and
 * body). Citation content hosts its pane-scoped AnnotatedPDFViewerProvider
 * here — above the header — so the title can follow the live active
 * annotation as the reader steps between passages.
 */
function ChatSideContentScope({
  content,
  children,
}: PropsWithChildren<{ content: ChatSideContent }>): ReactNode {
  switch (content.type) {
    case 'citationDocument':
      return (
        <CitationDocumentSideContentScope content={content}>
          {children}
        </CitationDocumentSideContentScope>
      );
    case 'documentSummary':
      return children;
    default:
      throw new UnreachableError({
        case: content,
        message: 'Unhandled chat side content type',
      });
  }
}

function ChatSideContentTitle({
  content,
}: {
  content: ChatSideContent;
}): ReactNode {
  switch (content.type) {
    case 'citationDocument':
      return <CitationDocumentSideContentTitle />;
    case 'documentSummary':
      return <DocumentSummarySideContentTitle content={content} />;
    default:
      throw new UnreachableError({
        case: content,
        message: 'Unhandled chat side content type',
      });
  }
}

function ChatSideContentBody({
  content,
}: {
  content: ChatSideContent;
}): ReactNode {
  switch (content.type) {
    case 'citationDocument':
      return <CitationDocumentSideContentBody />;
    case 'documentSummary':
      return <DocumentSummarySideContentBody content={content} />;
    default:
      throw new UnreachableError({
        case: content,
        message: 'Unhandled chat side content type',
      });
  }
}

interface ChatSideContentPaneProps {
  sx?: SxProps<Theme>;
}

/**
 * The expanded content pane that opens beside a chat transcript — the chrome
 * (header with title and close button) around whatever ChatSideContent is
 * currently open. Renders nothing while the pane is closed; hosts decide
 * where the pane sits and how wide it is.
 */
export function ChatSideContentPane({ sx }: ChatSideContentPaneProps) {
  const { sideContent, sideContentKey, closeSideContent } =
    useChatSideContentContext();

  if (!sideContent) {
    return null;
  }

  return (
    <Stack
      data-testid="chat-side-content-pane"
      sx={{
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        height: '100%',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Keyed on the open counter so replacing the content (another citation,
          a summary) remounts the scope and body with fresh state, like the
          citation modal's unmount between opens. */}
      <ChatSideContentScope key={sideContentKey} content={sideContent}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{
            borderBottom: `solid 1px ${COLORS.GRAY[200]}`,
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="title-s" noWrap>
            <ChatSideContentTitle content={sideContent} />
          </Typography>
          {/* xs with the shared header treatment, matching the chat column
              header's icon buttons so the two headers render the same height
              with aligned bottom borders. */}
          <CloseButton
            size="xs"
            variant="transparent"
            onClick={closeSideContent}
            sx={CHAT_HEADER_ICON_BUTTON_SX}
          />
        </Stack>
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <ChatSideContentBody content={sideContent} />
        </Box>
      </ChatSideContentScope>
    </Stack>
  );
}
